import { __decorate } from "tslib";
import { Group, Vector3 } from 'three';
import { FemurAnalyzerLTConfig as Config } from '../../Config/FemurAnalyzerLTConfig.js';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { Save, SaveChild } from '../../Core/Save.js';
import { ViewType } from '../../Models/AppModel.js';
import { Consts } from '../../Utils/Consts.js';
import { LimitUtils } from '../../Utils/LimitUtils.js';
import { Utils } from '../../Utils/Utils.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { DraggablePoint } from '../Primitive/DraggablePoint.js';
import { LabelAngle } from '../Primitive/Label/LabelAngle.js';
import { LimitLine } from '../Primitive/LimitLine.js';
import { LinePointToPoint2 } from '../Primitive/LinePointToPoint2.js';
import { Point } from '../Primitive/Point.js';
export class MechanicalAxisFemurLT extends Group {
    get angleForTemplating() {
        return VectorUtils.linesAngleFromOrigin(this.FH.position.clone().sub(this.TE.position), Consts.verDir);
    }
    get apexWithSign() {
        return 0;
    }
    get proximalDirToCalculateApex() {
        return this.FH.position.clone().sub(this.TE.position);
    }
    get distalDirToCalculateApex() {
        return this.FH.position.clone().sub(this.TE.position);
    }
    get mechAxisUpperPoint() {
        return this.FH.position;
    }
    get mechAxisLowerPoint() {
        return this.TE.position;
    }
    constructor(isLeft, isEOC) {
        super();
        this.pointsToRegister = 3;
        this._limitLines = [];
        this._enableLimits = false; // refresh page to make it works (to improve performance when don't use it)
        this.onAfterRestore = () => {
            this.update();
        };
        this.name = 'FemurAnalyzer';
        this._isLeft = isLeft;
        this._isEOC = isEOC;
        if (this._enableLimits) {
            this._limitLines[0] = new LimitLine(ViewType.LT, 0xffff00);
            this._limitLines[1] = new LimitLine(ViewType.LT, 0x00ffff);
        }
        this.add(this.FH = new DraggablePoint('FH', ViewType.LT, Config.FH_color), this.AE = new DraggablePoint('AE', ViewType.LT, Config.AE_color), this.PE = new DraggablePoint('PE', ViewType.LT, Config.PE_color), this.TE = new Point('TE', Config.TE_color), this.FH_TE_Line = new LinePointToPoint2('FH_TE_Line', ViewType.LT, Config.FH_TE_Line_color, this.FH, this.TE), this.AE_PE_Line = new LinePointToPoint2('AE_PE_Line', ViewType.LT, Config.AE_PE_Line_color, this.AE, this.PE), this.PDFALabel = new LabelAngle({
            name: 'PDFALabel',
            arcA: this.PE,
            arcB: this.FH,
            arcCenter: this.TE,
            viewType: ViewType.LT,
            labelAnchor: new Vector3(isLeft ? -7 : 7, 5),
            labelParams: { backgroundColor: Config.PDFALabel_backgroundColor, fontColor: Config.PDFALabel_fontColor },
            labelErrorParams: { backgroundColor: 'rgba(255, 0, 0, 0.6)', fontColor: '#ffffff' },
            minAngle: Consts.paleyAngles.LT.PDFA.min,
            maxAngle: Consts.paleyAngles.LT.PDFA.max,
            textCallback: (angle) => `PDFA ${angle}°`
        }), ...this._limitLines);
        this.TE.visible = false;
        this.bindEvents();
        this.PDFA = new CachedMeasure(() => {
            return this.FH.position.clone().sub(this.TE.position).angleTo(this.PE.position.clone().sub(this.AE.position));
        }, this.FH, this.TE);
    }
    bindEvents() {
        this.bindEvent('onAfterDragMove', () => {
            this.update();
        });
        this.bindEvent('onDragEnd', () => {
            this.clearLimits();
        });
        this.FH.bindEvent('onDragMove', (args) => {
            LimitUtils.applyLimitAndProjectVector(this.AE.position, this.PE.position, args.position, this.FH.position.clone().add(this.FH.position.clone().sub(this.AE.position)), this.FH.scale.x, this._limitLines[0]);
        });
        this.AE.bindEvent('onDragMove', (args) => {
            LimitUtils.applyLimitAndProjectVector(this.FH.position, this.PE.position, args.position, this.FH.position.clone().add(this._AE_PE_dir), this.AE.scale.x, this._limitLines[0]);
            this._AP && LimitUtils.applyLimitAndProjectVector(this._AP, this._PP, args.position, this.FH.position, this.AE.scale.x, this._limitLines[1]);
        });
        this.PE.bindEvent('onDragMove', (args) => {
            LimitUtils.applyLimitAndProjectVector(this.FH.position, this.AE.position, args.position, this.FH.position.clone().sub(this._AE_PE_dir), this.PE.scale.x, this._limitLines[0]);
            this._AP && LimitUtils.applyLimitAndProjectVector(this._AP, this._PP, args.position, this.FH.position, this.PE.scale.x, this._limitLines[1]);
        });
    }
    initPositions(positions, fixPosition) {
        fixPosition && this.fixPositionsOrder(positions);
        this.FH.position.copy(positions[0].setZ(0));
        this.AE.position.copy(positions[1].setZ(0));
        this.PE.position.copy(positions[2].setZ(0));
        this._AE_PE_dir = this.AE.position.clone().sub(this.PE.position).normalize();
        this.update();
    }
    fixPositionsOrder(positions) {
        const middlePointA = positions[0].clone();
        const middlePointB = positions[1].clone().lerp(positions[2], 0.5);
        const perpDir = middlePointA.clone().sub(middlePointB).cross(Consts.planeNormal);
        const sign = this._isLeft ? -1 : 1;
        if (Math.sign(positions[1].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(1, 2, positions);
        }
    }
    update() {
        this.TE.position.copy(this.AE.position.clone().lerp(this.PE.position, 1 / 3));
        this.dispatchEvent({ type: 'updated' });
    }
    clearLimits() {
        for (const limitLine of this._limitLines) {
            limitLine.visible = false;
        }
    }
    setTibiaLimits(AP, PP) {
        this._AP = AP;
        this._PP = PP;
    }
}
__decorate([
    Save("position")
], MechanicalAxisFemurLT.prototype, "FH", void 0);
__decorate([
    Save("position")
], MechanicalAxisFemurLT.prototype, "AE", void 0);
__decorate([
    Save("position")
], MechanicalAxisFemurLT.prototype, "PE", void 0);
__decorate([
    SaveChild()
], MechanicalAxisFemurLT.prototype, "PDFALabel", void 0);
__decorate([
    Save()
], MechanicalAxisFemurLT.prototype, "_AE_PE_dir", void 0);
//# sourceMappingURL=FemurAnalyzerLT.js.map